import React, { useState, useEffect, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const PortalViewer = ({ documents, clientName }) => {
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [toc, setToc] = useState([]);
  const [isDark, setIsDark] = useState(true);
  const contentRef = useRef(null);

  useEffect(() => {
    if (documents && documents.length > 0) {
      loadDocument(documents[0]);
    }
  }, [documents]);

  useEffect(() => {
    if (contentRef.current) {
      buildTableOfContents();
    }
  }, [content]);

  const loadDocument = async (doc) => {
    setSelectedDoc(doc);
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(doc.path);
      if (!response.ok) throw new Error('Failed to load document');
      const text = await response.text();
      setContent(text);
    } catch (err) {
      setError(`Unable to load document: ${err.message}`);
      setContent('');
    } finally {
      setLoading(false);
    }
  };

  const buildTableOfContents = () => {
    if (!contentRef.current) return;

    const headings = contentRef.current.querySelectorAll('h1, h2, h3');
    const items = Array.from(headings).map((h, index) => {
      const id = `heading-${index}`;
      h.id = id;
      return {
        id,
        level: parseInt(h.tagName.substring(1)),
        text: h.textContent
      };
    });
    setToc(items);
  };

  const scrollToHeading = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const toggleTheme = () => {
    setIsDark(!isDark);
  };

  const groupedDocs = documents.reduce((acc, doc) => {
    const category = doc.category || 'Documents';
    if (!acc[category]) acc[category] = [];
    acc[category].push(doc);
    return acc;
  }, {});

  return (
    <div className={`portal-viewer ${isDark ? 'dark' : 'light'}`}>
      <header className="portal-header">
        <div className="portal-brand">
          <h1>{clientName}</h1>
          <p className="portal-subtitle">Specification Review Portal</p>
        </div>
        <div className="portal-actions">
          <button onClick={handlePrint} className="portal-btn">
            📄 Print/PDF
          </button>
          <button onClick={toggleTheme} className="portal-btn">
            {isDark ? '☀️ Light' : '🌙 Dark'}
          </button>
        </div>
      </header>

      <div className="portal-container">
        <aside className="portal-sidebar">
          <nav className="portal-nav">
            {Object.entries(groupedDocs).map(([category, docs]) => (
              <div key={category} className="portal-nav-group">
                <div className="portal-nav-category">{category}</div>
                {docs.map((doc) => (
                  <a
                    key={doc.path}
                    href="#"
                    className={`portal-nav-link ${selectedDoc?.path === doc.path ? 'active' : ''}`}
                    onClick={(e) => {
                      e.preventDefault();
                      loadDocument(doc);
                    }}
                  >
                    {doc.title}
                  </a>
                ))}
              </div>
            ))}
          </nav>
        </aside>

        <main className="portal-content">
          <article className="portal-article" ref={contentRef}>
            {loading && <div className="portal-loading">Loading document...</div>}
            {error && <div className="portal-error">{error}</div>}
            {!loading && !error && content && (
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {content}
              </ReactMarkdown>
            )}
          </article>

          <aside className="portal-toc">
            {toc.length > 0 && (
              <>
                <div className="portal-toc-title">On this page</div>
                {toc.map((item) => (
                  <a
                    key={item.id}
                    href={`#${item.id}`}
                    className={`portal-toc-link level-${item.level}`}
                    onClick={(e) => {
                      e.preventDefault();
                      scrollToHeading(item.id);
                    }}
                  >
                    {item.text}
                  </a>
                ))}
              </>
            )}
          </aside>
        </main>
      </div>

      <footer className="portal-footer">
        <p>Portal powered by <span className="portal-branding-text">Omnigeist</span></p>
      </footer>
    </div>
  );
};

export default PortalViewer;

